import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Tooltip,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { toast } from 'react-toastify'
import TipTapEditor from 'src/mentor/components/TherapyReportMakingPage/components/TipTapEditor'
import { generateCertificate } from '../medicalCertificateService'
import {
  buildAutoTokenValues,
  buildFieldDisplayValues,
  getMissingRequiredFields,
  resolveDoctorName,
  substitutePlaceholders,
  COLOR_MODE,
  ACCENT_COLOR_PRESETS,
  loadColorPrefs,
  saveColorPrefs,
} from '../certificateUtils'

/**
 * Step 2 of the create flow. Renders the template's editable fields, keeps the rich-text body in
 * sync with those fields (until the practitioner manually edits the body), shows a live letterhead
 * preview, and on Generate sends the fully-substituted body to the backend for server-side PDF +
 * S3 storage.
 */
export default function CertificateComposer({
  template,
  patient,
  mentorProfile,
  clinicName,
  clinicId,
  clinicBrandId,
  bookedEventId,
  onGenerated,
  onBack,
}) {
  const [title, setTitle] = useState(template?.title || '')
  const [fieldValues, setFieldValues] = useState(() => {
    const init = {}
    ;(template?.editableFields || []).forEach((f) => {
      if (f.defaultValue) init[f.token] = f.defaultValue
    })
    return init
  })
  const [bodyHtml, setBodyHtml] = useState('')
  const [useLetterhead, setUseLetterhead] = useState(true)
  const initialColorPrefs = useMemo(() => loadColorPrefs(), [])
  const [colorMode, setColorMode] = useState(initialColorPrefs.colorMode)
  const [accentColor, setAccentColor] = useState(initialColorPrefs.accentColor)
  const [electronicallyGenerated, setElectronicallyGenerated] = useState(false)
  const [notValidForMedicoLegal, setNotValidForMedicoLegal] = useState(false)
  const [showCertificateNumber, setShowCertificateNumber] = useState(false)
  const [qrValidationEnabled, setQrValidationEnabled] = useState(false)
  const [viewAccessPolicy, setViewAccessPolicy] = useState('ALLOW_ALL')
  const [allowedMobilesText, setAllowedMobilesText] = useState('')
  const [generating, setGenerating] = useState(false)
  const manuallyEditedRef = useRef(false)
  const isBw = colorMode === COLOR_MODE.BW

  const autoTokens = useMemo(
    () => buildAutoTokenValues({ mentorProfile, patient, clinicName }),
    [mentorProfile, patient, clinicName],
  )

  // Recompute the substituted body from template + tokens + fields, unless the body was hand-edited.
  const computeBody = () => {
    const displayFields = buildFieldDisplayValues(template?.editableFields, fieldValues)
    return substitutePlaceholders(template?.bodyHtml, { ...autoTokens, ...displayFields })
  }

  useEffect(() => {
    if (!manuallyEditedRef.current) {
      setBodyHtml(computeBody())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues, template, autoTokens])

  const handleFieldChange = (token, value) => {
    setFieldValues((prev) => ({ ...prev, [token]: value }))
  }

  const handleBodyChange = (html) => {
    manuallyEditedRef.current = true
    setBodyHtml(html)
  }

  const resetBodyFromFields = () => {
    manuallyEditedRef.current = false
    setBodyHtml(computeBody())
  }

  const handleGenerate = async () => {
    const missing = getMissingRequiredFields(template?.editableFields, fieldValues)
    if (missing.length) {
      toast.error(`Please fill: ${missing.map((m) => m.label).join(', ')}`)
      return
    }
    if (!patient?.patientId) {
      toast.error('No patient selected')
      return
    }
    if (!String(bodyHtml || '').replace(/<[^>]*>/g, '').trim()) {
      toast.error('Certificate body cannot be empty')
      return
    }
    // Remember the chosen scheme as the practitioner's default for future certificates.
    saveColorPrefs({ colorMode, accentColor })
    setGenerating(true)
    const tst = toast.loading('Generating certificate…')
    try {
      const res = await generateCertificate(
        {
          templateId: template.id,
          title,
          bodyHtml,
          fieldValues,
          patientId: patient.patientId,
          bookedEventId: bookedEventId || patient.bookedEventId || null,
          clinicId: clinicId || null,
          clinicBrandId: clinicBrandId || null,
          useLetterhead,
          colorMode,
          accentColor,
          electronicallyGenerated,
          notValidForMedicoLegal,
          showCertificateNumber,
          qrValidationEnabled,
          viewAccessPolicy,
          allowedViewerMobiles:
            qrValidationEnabled && viewAccessPolicy === 'WHITELIST'
              ? allowedMobilesText
                  .split(/[\n,]+/)
                  .map((s) => s.trim())
                  .filter(Boolean)
              : [],
        },
        tst,
      )
      if (res.ok && res.certificate) {
        toast.update(tst, { render: 'Certificate generated', type: 'success', isLoading: false, autoClose: 2500 })
        onGenerated(res.certificate)
      } else {
        toast.update(tst, { render: res.message || 'Failed to generate', type: 'error', isLoading: false, autoClose: 4000 })
      }
    } catch (e) {
      toast.update(tst, { render: 'Failed to generate certificate', type: 'error', isLoading: false, autoClose: 4000 })
    } finally {
      setGenerating(false)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: 'none' }}>
          Templates
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* Left: form + editor */}
        <Box sx={{ flex: 1, minWidth: 320, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Document title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            size="small"
            fullWidth
          />

          <FormControlLabel
            control={<Switch checked={useLetterhead} onChange={(e) => setUseLetterhead(e.target.checked)} />}
            label="Use my letterhead (prescription pad)"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={electronicallyGenerated}
                onChange={(e) => setElectronicallyGenerated(e.target.checked)}
              />
            }
            label="Electronically generated — no signature needed"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={notValidForMedicoLegal}
                onChange={(e) => setNotValidForMedicoLegal(e.target.checked)}
              />
            }
            label="Not valid for Medico-Legal purposes"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showCertificateNumber}
                onChange={(e) => setShowCertificateNumber(e.target.checked)}
              />
            }
            label="Show certificate number"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={qrValidationEnabled}
                onChange={(e) => setQrValidationEnabled(e.target.checked)}
              />
            }
            label="Add QR to verify on portal"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
          />
          {qrValidationEnabled && (
            <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Who can view this document?</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={viewAccessPolicy}
                onChange={(_e, v) => v && setViewAccessPolicy(v)}
              >
                <ToggleButton value="OPEN" sx={{ textTransform: 'none', px: 1.5 }}>
                  Open (no OTP)
                </ToggleButton>
                <ToggleButton value="ALLOW_ALL" sx={{ textTransform: 'none', px: 1.5 }}>
                  Anyone (OTP)
                </ToggleButton>
                <ToggleButton value="WHITELIST" sx={{ textTransform: 'none', px: 1.5 }}>
                  Restricted
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                {viewAccessPolicy === 'OPEN'
                  ? 'Anyone who scans can view after entering their contact details (no OTP). The access is logged.'
                  : viewAccessPolicy === 'WHITELIST'
                    ? 'Only the listed numbers (and the patient) can view, after an OTP on WhatsApp.'
                    : 'Any mobile can view after an OTP on WhatsApp.'}
              </Typography>
              {viewAccessPolicy === 'WHITELIST' && (
                <TextField
                  label="Allowed mobile numbers (one per line)"
                  value={allowedMobilesText}
                  onChange={(e) => setAllowedMobilesText(e.target.value)}
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder={'+91 98765 43210\n+91 91234 56789'}
                  helperText="The patient's own number is always allowed."
                />
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={colorMode}
              onChange={(_e, value) => value && setColorMode(value)}
            >
              <ToggleButton value={COLOR_MODE.COLOR} sx={{ textTransform: 'none', px: 1.5 }}>
                Colour
              </ToggleButton>
              <ToggleButton value={COLOR_MODE.BW} sx={{ textTransform: 'none', px: 1.5 }}>
                Black &amp; White
              </ToggleButton>
            </ToggleButtonGroup>

            {!isBw && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mr: 0.5 }}>Accent</Typography>
                {ACCENT_COLOR_PRESETS.map((preset) => (
                  <Tooltip key={preset.value} title={preset.label}>
                    <Box
                      onClick={() => setAccentColor(preset.value)}
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: preset.value,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor:
                          accentColor?.toUpperCase() === preset.value.toUpperCase() ? 'text.primary' : 'transparent',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                      }}
                    />
                  </Tooltip>
                ))}
                <Tooltip title="Custom colour">
                  <Box
                    component="input"
                    type="color"
                    value={accentColor || '#1F4E79'}
                    onChange={(e) => setAccentColor(e.target.value)}
                    sx={{
                      width: 24,
                      height: 24,
                      p: 0,
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                    }}
                  />
                </Tooltip>
              </Box>
            )}
          </Box>

          {(template?.editableFields || []).length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 1.5 }}>
              {template.editableFields.map((field) => (
                <TextField
                  key={field.token}
                  label={field.label + (field.required ? ' *' : '')}
                  value={fieldValues[field.token] || ''}
                  onChange={(e) => handleFieldChange(field.token, e.target.value)}
                  type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                  multiline={field.type === 'textarea'}
                  minRows={field.type === 'textarea' ? 2 : undefined}
                  placeholder={field.placeholder || ''}
                  size="small"
                  fullWidth
                  InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                />
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
              Document text
            </Typography>
            <Tooltip title="Regenerate the text from the fields above (discards manual edits)">
              <Button size="small" startIcon={<RestartAltIcon />} onClick={resetBodyFromFields} sx={{ textTransform: 'none' }}>
                Reset from fields
              </Button>
            </Tooltip>
          </Box>
          <TipTapEditor value={bodyHtml} onChange={handleBodyChange} placeholder="Certificate text…" />
        </Box>

        {/* Right: live letterhead preview */}
        <Box sx={{ flex: 1, minWidth: 320 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mb: 1 }}>
            Preview
          </Typography>
          <LetterheadPreview
            mentorProfile={mentorProfile}
            title={title}
            bodyHtml={bodyHtml}
            autoTokens={autoTokens}
            showLetterhead={useLetterhead}
            isBw={isBw}
            accentColor={accentColor}
            electronicallyGenerated={electronicallyGenerated}
            notValidForMedicoLegal={notValidForMedicoLegal}
          />
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 1, fontStyle: 'italic' }}>
            Your official prescription letterhead is applied automatically on the generated PDF.
          </Typography>
        </Box>
      </Box>

      <Divider />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        <Button onClick={onBack} sx={{ textTransform: 'none' }}>
          Back
        </Button>
        <Button variant="contained" onClick={handleGenerate} disabled={generating} sx={{ textTransform: 'none' }}>
          {generating ? 'Generating…' : 'Generate certificate'}
        </Button>
      </Box>
    </Box>
  )
}

function LetterheadPreview({
  mentorProfile,
  title,
  bodyHtml,
  autoTokens,
  showLetterhead = true,
  isBw = false,
  accentColor,
  electronicallyGenerated = false,
  notValidForMedicoLegal = false,
}) {
  const doctorName = resolveDoctorName(mentorProfile)
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2.5,
        bgcolor: '#fff',
        minHeight: 360,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}
    >
      {showLetterhead ? (
        <Box sx={{ textAlign: 'center', pb: 1, borderBottom: '2px solid', borderColor: 'text.primary' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{doctorName}</Typography>
          {mentorProfile?.specialization && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{mentorProfile.specialization}</Typography>
          )}
          {autoTokens?.doctorDegrees && (
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{autoTokens.doctorDegrees}</Typography>
          )}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 10.5, color: 'text.disabled', textAlign: 'center', pb: 1 }}>
          Plain paper (no letterhead)
        </Typography>
      )}
      {title && (
        <Typography
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            fontSize: 14,
            mt: 2,
            textTransform: 'uppercase',
            color: isBw ? '#000' : accentColor || 'text.primary',
          }}
        >
          {title}
        </Typography>
      )}
      <Box
        sx={{
          mt: 2,
          fontSize: 13.5,
          lineHeight: 1.6,
          '& p': { my: 0.75 },
          ...(isBw ? { filter: 'grayscale(1)', '& *': { color: '#000 !important' } } : {}),
        }}
        dangerouslySetInnerHTML={{ __html: bodyHtml || '' }}
      />
      <Box sx={{ mt: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2 }}>
        <Typography sx={{ fontSize: 12 }}>Date: {autoTokens?.issueDate}</Typography>
        <Box sx={{ textAlign: 'right', maxWidth: 260 }}>
          {electronicallyGenerated ? (
            <Typography sx={{ fontSize: 10.5, color: 'text.secondary', fontStyle: 'italic', mb: 1 }}>
              This is an electronically generated document and no signature is required.
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 2.5 }}>Signature</Typography>
          )}
          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{doctorName}</Typography>
        </Box>
      </Box>
      {notValidForMedicoLegal && (
        <Typography sx={{ fontSize: 10, color: 'text.disabled', fontStyle: 'italic', textAlign: 'center', mt: 3 }}>
          Not valid for Medico-Legal purposes.
        </Typography>
      )}
    </Box>
  )
}
